import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { getTodoTasksForVendors, PopupEventStatusEnum } from '@/api';
import { TODO_TASK_ACTIONS } from '@/constants';
import { getVendorIds, resolveGqlErrors } from '@/helpers';
import { useIdlePooling } from '@/hooks';
import { usePermissions } from '@/permissions';
import { DateTasks, TodoBucketItem, TodoTask } from '../types';
import { buildAvailabilitySummaries, getEventTaskDate } from './helpers';

type UseTodoSection = () => {
  loading: boolean;
  error?: string;
  tasksByDate: DateTasks[];
};

const TODO_EVENT_STATUSES = [PopupEventStatusEnum.SCHEDULED, PopupEventStatusEnum.ACTIVE];

const useTodoSection: UseTodoSection = () => {
  const { isAvailabilityOnly } = usePermissions();
  const vendorIds = getVendorIds();
  const expiresAfter = useMemo(() => new Date().toISOString(), []);
  const { loading, data, error, stopPolling, startPolling } = useQuery(getTodoTasksForVendors, {
    variables: { vendorIds, expiresAfter },
    skip: vendorIds.length === 0,
  });
  useIdlePooling({ startPolling, stopPolling });

  const allTasks = (data?.getTodoTasksForVendors?.todoTasks ?? []).toSorted(
    (a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
  );

  const eventTasks = allTasks
    .filter((task) => task.action === TODO_TASK_ACTIONS.CONFIRM_EVENT_ATTENDANCE)
    .map((task) => ({
      ...task,
      // Drop popup events whose status is no longer actionable.
      workItems: task.workItems.filter((item) => {
        if (item.__typename !== 'PopupEventVendor') return true;
        return TODO_EVENT_STATUSES.includes(
          item.popupEvent.status.toUpperCase() as PopupEventStatusEnum
        );
      }),
    })) as TodoTask[];

  const availabilityTasks = allTasks.filter(
    (task) => task.action === TODO_TASK_ACTIONS.SUBMIT_RESTAURANT_AVAILABILITY
  );
  const availabilitySummaries = buildAvailabilitySummaries(availabilityTasks);

  // Merge summaries and tasks into one date-keyed list. Summaries are placed first
  // within their date so they render above any same-day event task.
  const bucketsByDate = new Map<string, TodoBucketItem[]>();

  availabilitySummaries.forEach((summary) => {
    const date = dayjs(summary.expiresAt).format('YYYY-MM-DD');
    const items = bucketsByDate.get(date) ?? [];
    items.push({ type: 'summary', summary });
    bucketsByDate.set(date, items);
  });

  eventTasks.forEach((task) => {
    const date = getEventTaskDate(task);
    const items = bucketsByDate.get(date) ?? [];
    items.push({ type: 'task', task });
    bucketsByDate.set(date, items);
  });

  const allTasksByDate: DateTasks[] = Array.from(bucketsByDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, items]) => ({ date, items }));

  // The availability_submission role only ever acts on availability to-dos.
  const tasksByDate: DateTasks[] = isAvailabilityOnly
    ? allTasksByDate
        .map(({ date, items }) => ({
          date,
          items: items.filter((item) => item.type === 'summary'),
        }))
        .filter(({ items }) => items.length > 0)
    : allTasksByDate;

  return {
    loading,
    error: resolveGqlErrors(error || data?.getTodoTasksForVendors?.error),
    tasksByDate,
  };
};

export default useTodoSection;
